import { useState } from 'react';
import { CategoryIcon } from '../components/CategoryIcon.js';
import { EventDrawer } from '../components/EventDrawer.js';
import { FilterBar, type Filters, emptyFilters } from '../components/FilterBar.js';
import { SeverityPill } from '../components/SeverityPill.js';
import { useEvents } from '../hooks/useData.js';
import { fmtTime, timeAgo } from '../lib/format.js';

export function Events() {
  const since = Math.floor(Date.now() / 1000) - 30 * 86400;
  const { data } = useEvents({ since, limit: 1000 });
  const [filters, setFilters] = useState<Filters>(emptyFilters());
  const [eventId, setEventId] = useState<number | null>(null);

  const rows = (data ?? []).filter(
    (e) => filters.categories.has(e.category) && filters.severities.has(e.severity),
  );
  const countries = new Set((data ?? []).map((e) => e.countryCode).filter(Boolean));

  return (
    <div className="space-y-4 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">Logs</h1>
          <p className="text-xs text-[var(--color-muted)]">
            Monitoring {countries.size} regions · {(data ?? []).length} events ingested · live
          </p>
        </div>
        <FilterBar filters={filters} onChange={setFilters} />
      </div>
      <div className="overflow-hidden rounded-lg border border-[var(--color-border)]">
        <div className="indeterminate h-0.5 w-full" />
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-[var(--color-muted)]">
            <tr>
              <th className="px-3 py-2" />
              <th className="px-3 py-2">Severity</th>
              <th className="px-3 py-2">Title</th>
              <th className="px-3 py-2">Source</th>
              <th className="px-3 py-2">When</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((e) => (
              <tr
                key={e.id}
                onClick={() => setEventId(e.id)}
                onKeyDown={(ev) => ev.key === 'Enter' && setEventId(e.id)}
                tabIndex={0}
                className="cursor-pointer border-t border-[var(--color-border)] hover:bg-[var(--color-panel-2)]"
              >
                <td className="px-3 py-2">
                  <CategoryIcon category={e.category} />
                </td>
                <td className="px-3 py-2">
                  <SeverityPill severity={e.severity} />
                </td>
                <td className="px-3 py-2">{e.title}</td>
                <td className="mono px-3 py-2 text-xs uppercase text-[var(--color-muted)]">
                  {e.source}
                </td>
                <td
                  className="mono px-3 py-2 text-xs text-[var(--color-muted)]"
                  title={fmtTime(e.occurredAt)}
                >
                  {timeAgo(e.occurredAt)}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-[var(--color-muted)]">
                  No events. Run an ingest from Admin or wait for the cron.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <EventDrawer eventId={eventId} onClose={() => setEventId(null)} />
    </div>
  );
}
